'use client';

import { FormControlInputStyle } from '@/app/login/style/style';
import CheckPsd from '@/assets/icons/CheckPsd.svg';
import Closed from '@/assets/icons/Closed.svg';
import NoCheckPsd from '@/assets/icons/NoCheckPsd.svg';
import PasswordError from '@/assets/icons/login-error-logo.svg';
import PasswordSuccess from '@/assets/icons/passwordSuccess.svg';
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import style from './style.module.scss';

import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { PasswordValidation } from '@/app/login/type/index';
import { validateEmailPassword } from '@/app/login/utils/index';

import { useTranslation } from '@/i18n/client';
import { sendPv } from '@/tracks/25426';

interface PropsType {
  psdVal?: string;
  setPsdVal?: (v: string) => void;
  onSubmit?: () => void;
  psdValidatedHandle: (v: boolean) => void;
}

const Password = observer(({ psdVal, setPsdVal, onSubmit, psdValidatedHandle }: PropsType) => {
  const { t } = useTranslation();

  const [psdValue, setPsdValue] = useState<string>(''); // 密码
  const [showPsd, setShowPsd] = useState<boolean>(true); // 密码是否显示

  const [confirmPsdValue, setConfirmPsdValue] = useState(''); // 再次输入的密码
  const [confirmPsdIsCurrent, setConfirmPsdIsCurrent] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);

  const [passwordMsgFlag, setPasswordMsgFlag] = useState(false);
  const [passwordValid, setPasswordValid] = useState(true);

  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    minimum: {
      minimumValid: false,
      minimumMsg: t('login.signin.forgotPwd.setNewPwd.rules.rule1'),
    },
    atLeastOneNumber: {
      atLeastOneNumberValid: false,
      atLeastOneNumberMsg: t('login.signin.forgotPwd.setNewPwd.rules.rule2'),
    },
    atLeastOneLetter: {
      atLeastOneLetterValid: false,
      atLeastOneLetterMsg: t('login.signin.forgotPwd.setNewPwd.rules.rule3'),
    },
    atLeastOneSpecialCharacter: {
      atLeastOneSpecialCharacterValid: false,
      atLeastOneSpecialCharacterMsg: t('login.signin.forgotPwd.setNewPwd.rules.rule4'),
    },
  });

  useEffect(() => {
    sendPv.NewPassword();
    if (!!psdVal) {
      setPsdValue(psdVal);
      setConfirmPsdValue(psdVal);
    }
  }, [psdVal]);

  useEffect(() => {
    const {
      minimum: { minimumValid },
      atLeastOneNumber: { atLeastOneNumberValid },
      atLeastOneLetter: { atLeastOneLetterValid },
      atLeastOneSpecialCharacter: { atLeastOneSpecialCharacterValid },
    } = passwordValidation;
    const validate = !(
      minimumValid &&
      atLeastOneNumberValid &&
      atLeastOneLetterValid &&
      atLeastOneSpecialCharacterValid &&
      psdValue === confirmPsdValue
    );
    psdValidatedHandle(validate);
    !validate && setPsdVal && setPsdVal(psdValue);
  }, [psdValue, confirmPsdValue, passwordValidation]);

  // 监听第一次输入密码的时候
  useEffect(() => {
    passwordConfirmValidation(psdValue);
    confirmPsdValue && setConfirmPsdIsCurrent(psdValue === confirmPsdValue);
  }, [psdValue]);
  // 监听再次输入密码的时候
  useEffect(() => {
    psdValue && setConfirmPsdIsCurrent(psdValue === confirmPsdValue);
  }, [confirmPsdValue]);

  // 密码校验
  const passwordConfirmValidation = (psdValue: any) => {
    if (psdValue || /^\s+$/.test(psdValue)) {
      setPasswordMsgFlag(true);
      const {
        minimumValid,
        atLeastOneNumberValid,
        atLeastOneLetterValid,
        atLeastOneSpecialCharacterValid,
      } = validateEmailPassword(psdValue);
      setPasswordValidation((prevState) => {
        const mutableState = { ...prevState };
        mutableState.minimum.minimumValid = minimumValid;
        mutableState.atLeastOneNumber.atLeastOneNumberValid = atLeastOneNumberValid;
        mutableState.atLeastOneLetter.atLeastOneLetterValid = atLeastOneLetterValid;
        mutableState.atLeastOneSpecialCharacter.atLeastOneSpecialCharacterValid =
          atLeastOneSpecialCharacterValid;
        return mutableState;
      });
      console.log('passwordValidation=', passwordValidation);
      setPasswordValid(
        minimumValid &&
          atLeastOneNumberValid &&
          atLeastOneLetterValid &&
          atLeastOneSpecialCharacterValid,
      );
    } else {
      setPasswordValidation((prevState) => {
        const mutableState = { ...prevState };
        mutableState.minimum.minimumValid = false;
        mutableState.atLeastOneNumber.atLeastOneNumberValid = false;
        mutableState.atLeastOneLetter.atLeastOneLetterValid = false;
        mutableState.atLeastOneSpecialCharacter.atLeastOneSpecialCharacterValid = false;
        // 返回修改后的对象
        return mutableState;
      });
      setPasswordValid(true);
    }
  };
  // 查看、隐藏密码
  const handleClickShowPassword = () => setShowPsd((show) => !show);
  // 查看/隐藏二次密码
  const handleShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const submitKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      onSubmit && onSubmit();
    }
  };

  return (
    <>
      <FormControl
        margin='normal'
        color='info'
        error={passwordMsgFlag && !passwordValid}
        sx={{
          ...FormControlInputStyle,
        }}
        variant='outlined'
      >
        <InputLabel htmlFor='outlined-adornment-password'>{t('login.common.pwdTitle')}</InputLabel>
        <OutlinedInput
          id='outlined-adornment-password'
          value={psdValue}
          type={showPsd ? 'password' : 'text'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPsdValue(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => submitKeyDown(e)}
          autoComplete='new-password'
          sx={{
            "& input[type='password']::-ms-reveal": {
              display: 'none',
            },
          }}
          endAdornment={
            <InputAdornment position='end'>
              {psdValue ? (
                <>
                  <IconButton
                    onClick={() => {
                      setPsdValue('');
                    }}
                  >
                    <Closed />
                  </IconButton>
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                      event.preventDefault()
                    }
                    onMouseUp={(event: React.MouseEvent<HTMLButtonElement>) =>
                      event.preventDefault()
                    }
                  >
                    {showPsd ? <NoCheckPsd /> : <CheckPsd />}
                  </IconButton>
                </>
              ) : null}
            </InputAdornment>
          }
        />
      </FormControl>
      {passwordMsgFlag ? (
        <>
          {Object.entries(passwordValidation).map(([key, value]) => (
            <div key={key} className={value[`${key}Valid`] ? style.successText : style.errorText}>
              {value[`${key}Valid`] ? (
                <PasswordSuccess />
              ) : (
                <PasswordError style={{ width: 10, height: 10 }} />
              )}{' '}
              {value[`${key}Msg`]}
            </div>
          ))}
        </>
      ) : (
        ''
      )}
      {/* 再次输入邮箱密码 */}
      <FormControl
        color='info'
        sx={{
          ...FormControlInputStyle,
        }}
        variant='outlined'
        margin='normal'
        error={!!confirmPsdValue && !confirmPsdIsCurrent}
      >
        <InputLabel htmlFor='outlined-adornment-re-password'>
          {t('login.common.pwdReEnter')}
        </InputLabel>
        <OutlinedInput
          id='outlined-adornment-re-password'
          value={confirmPsdValue}
          type={showConfirmPassword ? 'password' : 'text'}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPsdValue(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => submitKeyDown(e)}
          autoComplete='new-password'
          endAdornment={
            <InputAdornment position='end'>
              {confirmPsdValue ? (
                <>
                  <IconButton
                    onClick={() => {
                      setConfirmPsdValue('');
                      psdValidatedHandle(true);
                    }}
                  >
                    <Closed />
                  </IconButton>
                  <IconButton
                    onClick={handleShowConfirmPassword}
                    onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                      event.preventDefault()
                    }
                    onMouseUp={(event: React.MouseEvent<HTMLButtonElement>) =>
                      event.preventDefault()
                    }
                  >
                    {showConfirmPassword ? <NoCheckPsd /> : <CheckPsd />}
                  </IconButton>
                </>
              ) : null}
            </InputAdornment>
          }
          sx={{
            "& input[type='password']::-ms-reveal": {
              display: 'none',
            },
          }}
        />
        {confirmPsdValue && (
          <div className={confirmPsdIsCurrent ? style.successText : style.errorText}>
            {confirmPsdIsCurrent ? (
              <PasswordSuccess />
            ) : (
              <PasswordError style={{ width: 10, height: 10 }} />
            )}
            {t('login.signin.forgotPwd.setNewPwd.rules.match')}
          </div>
        )}
      </FormControl>
    </>
  );
});

export default Password;
